'use client';

import {
  ClockCounterClockwise,
  PlusCircle,
  ArrowsClockwise,
  ProhibitInset,
  Trash,
  ShieldCheck,
  Syringe,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { Skeleton } from '@/components/ai-elements/skeleton';
import type { MemoryAuditView } from './types';

const OP_META: Record<string, { icon: Icon; label: string; tone: string }> = {
  add: { icon: PlusCircle, label: 'Added', tone: 'text-emerald-500' },
  update: { icon: ArrowsClockwise, label: 'Profile rebuilt', tone: 'text-violet-400' },
  invalidate: { icon: ProhibitInset, label: 'Invalidated', tone: 'text-amber-500' },
  hard_delete: { icon: Trash, label: 'Deleted scope', tone: 'text-destructive' },
  grant_change: { icon: ShieldCheck, label: 'Access changed', tone: 'text-sky-500' },
  injected: { icon: Syringe, label: 'Used in a turn', tone: 'text-muted-foreground' },
};

function actorLabel(actor: string): string {
  if (actor === 'system') return 'System';
  if (actor === 'user') return 'You';
  if (actor.startsWith('agent:')) return 'Agent';
  return actor;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface AuditTrailProps {
  audit: MemoryAuditView[];
  loading: boolean;
}

export function AuditTrail({ audit, loading }: AuditTrailProps) {
  return (
    <section className="rounded-2xl border border-border/70 bg-card/40 p-[18px]">
      <div className="flex items-center gap-2">
        <ClockCounterClockwise weight="duotone" className="size-4 text-violet-400" />
        <h2 className="text-sm font-extrabold tracking-tight">Activity</h2>
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        The last 50 memory events — every write, injection, and deletion, for full transparency.
      </p>

      <div className="mt-4 space-y-1">
        {loading ? (
          Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-9 w-full rounded-lg" />)
        ) : audit.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border/60 px-4 py-6 text-center text-[12px] text-muted-foreground">
            No memory activity yet.
          </p>
        ) : (
          audit.map((row) => {
            const meta = OP_META[row.op] ?? {
              icon: ClockCounterClockwise,
              label: row.op,
              tone: 'text-muted-foreground',
            };
            const Icon = meta.icon;
            const fact = typeof row.detail?.fact === 'string' ? (row.detail.fact as string) : null;
            return (
              <div
                key={row.id}
                className="flex items-center gap-3 rounded-lg px-2 py-1.5 text-[12px] hover:bg-accent/40"
              >
                <Icon weight="duotone" className={`size-4 shrink-0 ${meta.tone}`} />
                <span className="font-medium">{meta.label}</span>
                <span className="text-muted-foreground">by {actorLabel(row.actor)}</span>
                {fact && (
                  <span className="min-w-0 flex-1 truncate text-muted-foreground/70">“{fact}”</span>
                )}
                <span className="ml-auto shrink-0 text-[11px] text-muted-foreground/60">
                  {formatWhen(row.createdAt)}
                </span>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
